import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

// Idle auto-logout disabled per admin request, admins stay signed in indefinitely
const IDLE_EVENTS: string[] = [];

interface AuthContextType {
 user: User | null;
 session: Session | null;
 loading: boolean;
 isAdmin: boolean;
 signUp: (email: string, password: string, fullName: string, emailRedirectTo?: string) => Promise<{ error: Error | null }>;
 signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
 signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
 const ctx = useContext(AuthContext);
 if (!ctx) throw new Error("useAuth must be used within AuthProvider");
 return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
 const [user, setUser] = useState<User | null >(null);
 const [session, setSession] = useState<Session | null>(null);
 const [loading, setLoading] = useState(true);
 const [isAdmin, setIsAdmin] = useState(false);
 const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

 const checkAdminRole = async (userId: string) => {
 const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
 setIsAdmin(!!data);
 };

 // Idle auto-logout disabled, admins stay signed in indefinitely
 const resetIdleTimer = useCallback(() => {}, []);

 useEffect(() => {
 // Set up auth listener BEFORE checking session
 const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
 setSession(session);
 setUser(session?.user ?? null);
 if (session?.user) {
 setTimeout(() => checkAdminRole(session.user.id), 0);
 } else {
 setIsAdmin(false);
 }
 setLoading(false);
 });

 supabase.auth.getSession().then(({ data: { session } }) => {
 setSession(session);
 setUser(session?.user ?? null);
 if (session?.user) {
 checkAdminRole(session.user.id);
 }
 setLoading(false);
 });

 return () => subscription.unsubscribe();
 }, []);

 const signUp = async (email: string, password: string, fullName: string, emailRedirectTo?: string) => {
 const { error } = await supabase.auth.signUp({
 email,
 password,
 options: {
 data: { full_name: fullName },
 emailRedirectTo: emailRedirectTo ?? window.location.origin,
 },
 });
 if (!error) {
 window.dataLayer = window.dataLayer || [];
 window.dataLayer.push({ event: "sign_up", method: "email" });
 }
 return { error: error as Error | null };
 };

 const signIn = async (email: string, password: string) => {
 const { error } = await supabase.auth.signInWithPassword({ email, password });
 return { error: error as Error | null };
 };

 const signOut = async () => {
 await supabase.auth.signOut();
 };

 return (
 <AuthContext.Provider value={{ user, session, loading, isAdmin, signUp, signIn, signOut }}>
 {children}
 </AuthContext.Provider>
 );
};
